import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    Divider,
    Alert,
    Chip,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useToken } from "../items/UseToken";
import { SchedulersReadyState } from '../ReadyStates';


const cronRegex =
    /^(\*|([0-5]?\d))( (\*|([0-5]?\d))( (\*|([01]?\d|2[0-3]))( (\*|([01]?\d|2[0-3]))( (\*|([1-9]|[12]\d|3[01]))( (\*|([1-9]|1[0-2]))( (\*|([0-6]))?)?)?)?)?)?)?$/;

function decodeBase64Utf8(b64) {
    try {
        return decodeURIComponent(
            Array.prototype.map
                .call(atob(b64), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join("")
        );
    } catch {
        try {
            return atob(b64);
        } catch {
            return "";
        }
    }
}

function extractInputsFromInstructionBase64(instructionB64) {
    if (!instructionB64) return [];

    try {
        const json = decodeBase64Utf8(instructionB64);
        const root = JSON.parse(json);
        const vars = root?.variables;

        if (!Array.isArray(vars)) return [];

        return vars
            .filter((v) => v?.name && (v.aiInput === "required" || v.aiInput === "optional"))
            .map((v) => ({
                key: v.name,
                value: v.value ?? "",
                required: v.aiInput === "required"
            }));
    } catch {
        return [];
    }
}

export default function SchedulerTab({ emptyState }) {
    const { token } = useToken();

    // ===================== DATA =====================
    const [processes, setProcesses] = useState([]);
    const [machines, setMachines] = useState([]);
    const [schedules, setSchedules] = useState([]);

    // ===================== UI STATE =====================
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    // confirm delete
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [scheduleToDelete, setScheduleToDelete] = useState(null);

    // ===================== CREATE FORM =====================
    const [name, setName] = useState("");
    const [processId, setProcessId] = useState("");
    const [processGuid, setProcessGuid] = useState("");

    const [machineGuid, setMachineGuid] = useState("");
    const [machineName, setMachineName] = useState("");


    const [cron, setCron] = useState("*/5 * * * *"); // default: co 5 min
    const [timezone, setTimezone] = useState("Europe/Warsaw");

    const [description, setDescription] = useState("");

    const [inputs, setInputs] = useState([]);
    const [argumentsMap, setArgumentsMap] = useState({});

    // feedback
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    // =====================
    // FETCH
    // =====================
    const refreshSchedules = () => {
        fetch("api/scheduler", {
            headers: { Authorization: "Bearer " + token }
        })
            .then((r) => r.json())
            .then((r) => setSchedules(r.results || []))
            .catch(() => setSchedules([]));
    };

    useEffect(() => {
        fetch("api/getrobots?is_active=1", {
            headers: { Authorization: "Bearer " + token }
        })
            .then((r) => r.json())
            .then((r) => setProcesses(r.results || []));

        fetch("api/getmachines", {
            headers: { Authorization: "Bearer " + token }
        })
            .then((r) => r.json())
            .then((r) => setMachines(r.results || []));

        refreshSchedules();
    }, [token]);

    // =====================
    // INPUTS PER PROCESS (NO EXTRA FETCH)
    // =====================
    useEffect(() => {
        if (!processId) {
            setInputs([]);
            setArgumentsMap({});
            return;
        }

        const proc = processes.find((p) => p.id === processId);
        const extracted = extractInputsFromInstructionBase64(proc?.instruction);

        setInputs(extracted);

        // init arguments with defaults
        const initArgs = {};
        extracted.forEach((i) => {
            initArgs[i.key] = i.value ?? "";
        });
        setArgumentsMap(initArgs);
    }, [processId, processes]);

    // =====================
    // VALIDATION
    // =====================
    const isCronValid = cron && cron.length >= 5; // prosta walidacja (cronRegex jest tricky)
    const isFormValid =
        isCreatingNew &&
        name.length >= 3 &&
        processId &&
        machineGuid &&
        cron &&
        isCronValid;

    // =====================
    // PREVIEW
    // =====================
    const selectedProcess = useMemo(() => {
        return processes.find((p) => p.id === processId) || null;
    }, [processId, processes]);

    const schedulePreview = useMemo(() => {
        if (!selectedProcess) return null;

        return {
            processName: selectedProcess.name,
            processVersion: selectedProcess.version,
            machineName: machines.find((m) => m.guid === machineGuid)?.name || "",
            cron,
            timezone,
            args: argumentsMap
        };
    }, [selectedProcess, machines, machineGuid, cron, timezone, argumentsMap]);

    // =====================
    // ACTIONS
    // =====================
    const handleSelectSchedule = (s) => {
        setSelectedSchedule(s);
        setIsCreatingNew(false);
        setError("");
        setSuccessMsg("");
    };

    const handleNewSchedule = () => {
        setIsCreatingNew(true);
        setSelectedSchedule(null);
        setError("");
        setSuccessMsg("");

        setName("");
        setProcessId("");
        setMachineGuid("");
        setCron("*/5 * * * *");
        setTimezone("Europe/Warsaw");
        setDescription("");
        setInputs([]);
        setArgumentsMap({});
    };

    const handleCreate = () => {
        setError("");
        setSuccessMsg("");

        fetch("api/scheduler", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: "Bearer " + token
            },
            body: JSON.stringify({
                name,
                process_id: processId,
                process_guid: processGuid,
                machine_guid: machineGuid,
                machine_name: machineName,
                cron,
                timezone,
                description,
                arguments: argumentsMap
            })
        })
            .then(async (r) => {
                if (!r.ok) throw new Error(await r.text());
                return r.json();
            })
            .then(() => {
                setSuccessMsg("Schedule created.");
                refreshSchedules();
                setIsCreatingNew(false);
            })
            .catch((err) => setError(err.message || "Create failed"));
    };

    const handleAskDelete = (s) => {
        setScheduleToDelete(s);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirmed = () => {
        if (!scheduleToDelete) return;

        fetch(`api/scheduler/${scheduleToDelete.guid}`, {
            method: "DELETE",
            headers: {
                Authorization: "Bearer " + token
            }
        })
            .then(async (r) => {
                if (!r.ok) throw new Error(await r.text());
                return r.json();
            })
            .then(() => {
                setDeleteDialogOpen(false);
                setScheduleToDelete(null);

                if (selectedSchedule?.guid === scheduleToDelete.guid) {
                    setSelectedSchedule(null);
                }

                refreshSchedules();
            })
            .catch((err) => {
                setDeleteDialogOpen(false);
                setError(err.message || "Delete failed");
            });
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch { }
    };

    const showEmpty = schedules.length === 0;


    // =====================
    // CRON PRESETS
    // =====================
    const cronPresets = [
        { label: "Every 5 min", value: "*/5 * * * *" },
        { label: "Every 15 min", value: "*/15 * * * *" },
        { label: "Every hour", value: "0 * * * *" },
        { label: "Daily 09:00", value: "0 9 * * *" },
        { label: "Weekly Sat 10:00", value: "0 10 * * 6" }
    ];

    // =====================
    // RENDER
    // =====================
    return (
         <>
            {showEmpty ? (<Box sx={{ p: 4 }}>
                {emptyState}
            </Box>) :
                <SchedulersReadyState/>
        }
        <Grid container spacing={2}>
            {/* ================= LIST ================= */}
            <Grid item xs={12}>
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                            <Typography variant="h6">Scheduled Jobs</Typography>

                            <Box display="flex" gap={1}>
                                <Button variant="outlined" onClick={refreshSchedules}>
                                    Refresh
                                </Button>
                                <Button startIcon={<AddIcon />} variant="contained" onClick={handleNewSchedule}>
                                    New Schedule
                                </Button>
                            </Box>
                        </Box>

                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                        <Table size="small" sx={{ mt: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Process</TableCell>
                                    <TableCell>Machine</TableCell>
                                    <TableCell>Cron</TableCell>
                                    <TableCell>Next Run</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {schedules.map((s) => (
                                    <TableRow
                                        key={s.guid}
                                        hover
                                        sx={{ cursor: "pointer" }}
                                        onClick={() => handleSelectSchedule(s)}
                                        selected={selectedSchedule?.guid === s.guid}
                                    >
                                        <TableCell>{s.name}</TableCell>
                                        <TableCell>{s.process_name || s.process_id}</TableCell>
                                        <TableCell>{s.machine || s.machine_guid}</TableCell>
                                        <TableCell>
                                            <code>{s.cron}</code>
                                        </TableCell>
                                        <TableCell>
                                            {s.next_run_utc ? new Date(s.next_run_utc).toLocaleString() : "-"}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Delete schedule">
                                                <IconButton
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAskDelete(s);
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {schedules.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Typography variant="body2">No schedules yet.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </Grid>

            {/* ================= PREVIEW ================= */}
            <Grid item xs={12} md={6}>
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {selectedSchedule
                                ? `Schedule Preview: ${selectedSchedule.name}`
                                : isCreatingNew
                                    ? "New Schedule Preview"
                                    : "Select a schedule"}
                        </Typography>

                        {(selectedSchedule || isCreatingNew) && (
                            <>
                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2">Cron</Typography>
                                <Box sx={{ fontFamily: "monospace", bgcolor: "#111", color: "#0f0", p: 1, borderRadius: 1 }}>
                                    {isCreatingNew ? cron : selectedSchedule.cron}
                                </Box>

                                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                                    Inputs
                                </Typography>

                                <Box sx={{ mt: 1 }}>
                                    {inputs.length === 0 ? (
                                        <Typography variant="body2">No inputs required</Typography>
                                    ) : (
                                        inputs.map((i) => (
                                            <Chip
                                                key={i.key}
                                                label={i.required ? `${i.key} *` : i.key}
                                                size="small"
                                                sx={{ mr: 1, mb: 1 }}
                                            />
                                        ))
                                    )}
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    Example payload
                                    <Tooltip title="Copy payload">
                                        <IconButton size="small" onClick={() => copy(JSON.stringify(argumentsMap, null, 2))}>
                                            <ContentCopyIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>

                                <Box
                                    sx={{
                                        mt: 1,
                                        fontFamily: "monospace",
                                        bgcolor: "#111",
                                        color: "#0f0",
                                        p: 1,
                                        borderRadius: 1,
                                        whiteSpace: "pre-wrap"
                                    }}
                                >
                                    {JSON.stringify(argumentsMap, null, 2)}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* ================= CREATE (BOTTOM, NOT LOOK LIKE EDIT) ================= */}
            {isCreatingNew && (
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6">Create New Schedule</Typography>

                            {successMsg && <Alert severity="success" sx={{ mt: 2 }}>{successMsg}</Alert>}

                            <TextField
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Schedule Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />

                            <TextField
                                select
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Process"
                                value={processId}
                                SelectProps={{ native: true }}
                                onChange={(e) => {
                                    const id = e.target.value;
                                    setProcessId(id);

                                    const selected = processes.find(p => p.id === id);
                                    setProcessGuid(selected?.guid || "");
                                }}
                            >
                                <option value="" />
                                {processes.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} v{p.version}
                                    </option>
                                ))}
                            </TextField>

                            <TextField
                                select
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Machine"
                                value={machineGuid}
                                SelectProps={{ native: true }}
                                onChange={(e) => {
                                    const guid = e.target.value;
                                    setMachineGuid(guid);

                                    const selected = machines.find(m => m.guid === guid);
                                    setMachineName(selected?.name || "");
                                }}
                            >
                                <option value="" />
                                {machines.map((m) => (
                                    <option key={m.guid} value={m.guid}>
                                        {m.name}
                                    </option>
                                ))}
                            </TextField>


                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2">Cron presets</Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                {cronPresets.map((p) => (
                                    <Chip
                                        key={p.value}
                                        label={p.label}
                                        clickable
                                        onClick={() => setCron(p.value)}
                                        color={cron === p.value ? "primary" : "default"}
                                    />
                                ))}
                            </Box>

                            <TextField
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Cron expression"
                                value={cron}
                                error={!!cron && !isCronValid}
                                helperText="Example: */5 * * * *  (every 5 minutes)"
                                onChange={(e) => setCron(e.target.value)}
                            />

                            <TextField
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Timezone"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                            />

                            <TextField
                                fullWidth
                                sx={{ mt: 2 }}
                                label="Description"
                                multiline
                                rows={2}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />

                            {/* INPUT FIELDS */}
                            {processId && inputs.length > 0 && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2">Arguments</Typography>

                                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        {inputs.map((input) => (
                                            <Grid item xs={12} key={input.key}>
                                                <TextField
                                                    fullWidth
                                                    label={input.required ? `${input.key} *` : input.key}
                                                    value={argumentsMap[input.key] ?? ""}
                                                    onChange={(e) =>
                                                        setArgumentsMap((prev) => ({
                                                            ...prev,
                                                            [input.key]: e.target.value
                                                        }))
                                                    }
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            )}

                            <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                                <Button variant="outlined" onClick={() => setIsCreatingNew(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" disabled={!isFormValid} onClick={handleCreate}>
                                    Create Schedule
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            )}

            {/* ================= DELETE DIALOG ================= */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Schedule?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete schedule <b>{scheduleToDelete?.name}</b>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteConfirmed}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            </Grid>
        </>
    );
}
